// The mail client's view of the letters (R-M6): letters of the store as rows, folders and their counts, the chain of a letter,
// search with «from: to: has: is:» like HomeMail, and the commands with their optimistic predictions.

import type { Id, RecordObj } from '@confdeck/core-shared';
import type { SyncClient } from '../../sync/client.ts';

export type FolderRole = 'inbox' | 'sent' | 'drafts' | 'archive' | 'trash' | 'junk';
/** What the left column offers: the folders, «Важное» (a mark, not a folder) and the service letters of the system. */
export type View = FolderRole | 'starred';

export const FOLDER_LIST: { key: View; label: string; icon: string }[] = [
  { key: 'inbox', label: 'Входящие', icon: 'inbox' },
  { key: 'starred', label: 'Важное', icon: 'star' },
  { key: 'sent', label: 'Отправленные', icon: 'send' },
  { key: 'drafts', label: 'Черновики', icon: 'draft' },
  { key: 'archive', label: 'Архив', icon: 'archive' },
  { key: 'junk', label: 'Спам', icon: 'junk' },
  { key: 'trash', label: 'Корзина', icon: 'trash' },
];

export interface Attachment { name: string; size: number; type: string; url: string }

export interface Letter {
  id: Id; rec: RecordObj; mailboxId: Id | null; folder: FolderRole; subject: string; from: string; fromName: string;
  to: string; cc: string; date: string; snippet: string; unread: boolean; starred: boolean; thread: string;
  attachments: Attachment[]; linked: Id[]; status: string | null; error: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

export function letterOf(rec: RecordObj, mailboxId: Id | null): Letter {
  const d = rec.data;
  let attachments: Attachment[] = [];
  try { attachments = d.attachments ? JSON.parse(str(d.attachments)) as Attachment[] : []; } catch { attachments = []; }
  return {
    id: rec.id, rec, mailboxId, folder: (str(d.folder) || 'inbox') as FolderRole, subject: str(d.subject), from: str(d.from), fromName: str(d.from_name),
    to: str(d.to), cc: str(d.cc), date: str(d.date) || rec.createdAt, snippet: str(d.snippet), unread: !!d.unread, starred: !!d.starred,
    thread: str(d.thread) || rec.id, attachments, linked: str(d.linked).split(' ').filter(Boolean),
    status: str(d.status) || null, error: str(d.error) || null,
  };
}

export const inView = (l: Letter, v: View) => (v === 'starred' ? l.starred && l.folder !== 'trash' && l.folder !== 'junk' : l.folder === v);
export const outgoing = (l: Letter) => l.folder === 'sent' || l.folder === 'drafts';

/** «Имя <a@b>, c@d» → the names (or addresses) for the list and the reader. */
export function people(text: string): { name: string; address: string }[] {
  return text.split(',').map((p) => p.trim()).filter(Boolean).map((p) => {
    const m = /^(.*)<([^>]+)>$/.exec(p);
    return m ? { name: m[1]!.trim().replace(/^"|"$/g, '') || m[2]!, address: m[2]!.trim() } : { name: p, address: p };
  });
}
export const senderOf = (l: Letter) => (outgoing(l) ? `Кому: ${people(l.to).map((p) => p.name).join(', ') || '—'}` : l.fromName || l.from || '—');

/** Search like HomeMail: words in the subject, the people and the snippet, plus from:, to:, has:вложение, is:unread / is:непрочитанные. */
export function matches(l: Letter, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  for (const token of q.split(/\s+/)) {
    const [k, v = ''] = token.includes(':') ? token.split(/:(.*)/) as [string, string] : ['', token];
    if (k === 'from') { if (!`${l.fromName} ${l.from}`.toLowerCase().includes(v)) return false; continue; }
    if (k === 'to') { if (!`${l.to} ${l.cc}`.toLowerCase().includes(v)) return false; continue; }
    if (k === 'has') { if (!l.attachments.length) return false; continue; }
    if (k === 'is') { if ((v.startsWith('unread') || v.startsWith('непрочит')) && !l.unread) return false; if ((v.startsWith('star') || v.startsWith('важн')) && !l.starred) return false; continue; }
    if (![l.subject, l.fromName, l.from, l.to, l.cc, l.snippet, ...l.attachments.map((a) => a.name)].some((x) => x.toLowerCase().includes(token))) return false;
  }
  return true;
}

/** The time in the list: today — hours, this year — day and month, earlier — the full date. */
export function listTime(iso: string): string {
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '');
  return d.toLocaleDateString('ru-RU');
}
export const fullTime = (iso: string) => new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
export const fileSize = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} МБ` : n >= 1024 ? `${Math.round(n / 1024)} КБ` : `${n} Б`);

const now = () => new Date().toISOString();
const patch = (ids: Id[], data: Record<string, unknown>) => ids.map((id) => ({ kind: 'record' as const, id, op: 'patch' as const, data: { data, updatedAt: now() } }));

export function mark(client: SyncClient, ids: Id[], change: { unread?: boolean; starred?: boolean }) {
  if (ids.length) client.mutate('mail.mark', { ids, ...change }, patch(ids, change));
}
export function move(client: SyncClient, ids: Id[], folder: 'inbox' | 'archive' | 'trash' | 'junk') {
  if (ids.length) client.mutate('mail.move', { ids, folder }, patch(ids, { folder }));
}

/** `markdown` — the blocks of a letter written here (a draft opens with them); null for a letter read from a mailbox. */
export interface Body { text: string; html: string | null; markdown?: string | null }
const bodies = new Map<Id, Promise<Body>>();
/** The text of a letter, read once per session: the list keeps only the snippet (HomeMail: the body is fetched on opening). */
export function bodyOf(id: Id, fresh = false): Promise<Body> {
  let p = fresh ? undefined : bodies.get(id);
  if (!p) {
    p = fetch(`/api/mail/letters/${id}/body`).then(async (r) => { if (!r.ok) throw new Error('Письмо не загрузилось'); return (await r.json()) as Body; });
    p.catch(() => bodies.delete(id));
    bodies.set(id, p);
  }
  return p;
}
